export type OauthCallbackTokens = {
  accessToken: string;
  refreshToken: string;
};

/**
 * Read the token pair the API left in the URL fragment after a provider sign-in.
 *
 * The fragment is used rather than the query string because a fragment is never sent to a server, never
 * written to an access log, and is not carried by `Referer` (R0148).
 *
 * Returns `null` for anything that is not a complete pair. A half-filled fragment is not a session, and
 * storing an access token with no refresh token would produce an account that works for fifteen minutes
 * and then logs itself out.
 */
export const readOauthCallback = (fragment: string): OauthCallbackTokens | null => {
  const parameters = new URLSearchParams(fragment.startsWith('#') ? fragment.slice(1) : fragment);
  const accessToken = parameters.get('accessToken');
  const refreshToken = parameters.get('refreshToken');

  if (accessToken === null || refreshToken === null || accessToken === '' || refreshToken === '') {
    return null;
  }

  return { accessToken, refreshToken };
};

/**
 * The error a provider hands back when somebody declines the consent screen, or when the API refuses.
 *
 * Worth reading separately: "you pressed cancel" and "something is broken" both arrive as a failed
 * sign-in, and only one of them is worth showing as a problem.
 */
export const readOauthError = (fragment: string): string | null => {
  const parameters = new URLSearchParams(fragment.startsWith('#') ? fragment.slice(1) : fragment);

  return parameters.get('error');
};
